'use client';

import { useMemo } from 'react';
import { useLocale } from 'next-intl';
import { KafkaNote } from '@nf/utils-common/constants';
import { SiteConfigsSettingsData } from '@nf/types/site';
import { isWithinTimeRange } from '@nf/utils-common/date-time';

import { useRouter } from '@/navigation';
import { useSiteStore } from '@/store/site';
import { sportService } from '@/services/sport.service';
import { switchViewService } from '@/services/swtich-view.service';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useGoToWcPromotionMenuUrl } from '@/domains/wc-promotion/hooks/use-wc-promotion-menu-url';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { useWcPromotionVisible } from '@/domains/wc-promotion/hooks/use-wc-promotion-visible';
import { useWcPromotionActivityNavigation } from '@/domains/wc-promotion/hooks/use-wc-promotion-activity-navigation';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';

interface UsePopupBannersOptions {
	isfromLoginBanner?: boolean;
}

export const usePopupBanners = ({ isfromLoginBanner }: UsePopupBannersOptions = {}) => {
	const router = useRouter();
	const locale = useLocale();
	const [setCurrentPage] = useSiteStore(state => [state.setCurrentPage]);

	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const {
		enableESoccerBattlePromotion,
	} = useLicSettings();
	const { openPredictionMarket, enablePredictionMarketPopup } = usePredictionMarket();
	const goToWcPromotionMenuUrl = useGoToWcPromotionMenuUrl();
	const isWcPromotionVisible = useWcPromotionVisible();
	const [activeFilterBetBuilderPlus] = useSiteStore(state => [state.activeFilterBetBuilderPlus]);
	const { navigateToWcPromotionActivity } = useWcPromotionActivityNavigation();

	const { switchToCorrectScore } = useOddsPageSwitcher();

	const shortCode = useMemo(() => {
		return switchViewService.convertLanguageToShortCode(user?.Lang as string, SiteConfigsData as SiteConfigsSettingsData);
	}, [user?.Lang, SiteConfigsData]);

	return useMemo(() => {
		const navigateToSports = (path: string) => {
			const sportIdMatch = path.match(/\/sports\/(\d+)/);
			const sportId = sportIdMatch?.[1] ? Number(sportIdMatch[1]) : undefined;
			if (sportId && sportService.isAnyVirtualSportsProduct(sportId)) {
				setCurrentPage('lbl_VirtualSport');
			}
			router.push(path, { locale });
		};

		// NOTE: 圖片跟 lobby banner 共用，所以預設放在 /images/banner/lobby/{專案資料夾}/{name}-{shortCode}.jpg
		return [
			{
				name: 'world-cup',
				href: `/images/banner/lobby/world-cup/world-cup-${shortCode}.jpg`,
				fallbackHref: '/images/banner/lobby/world-cup/world-cup-en.jpg',
				enabled: isWcPromotionVisible,
				navigate: () => {
					setKafkaNormal({
						note: isfromLoginBanner ? KafkaNote.WcPromotion2026 : KafkaNote.WcPromotionHub,
						type: 1,
						action: isfromLoginBanner ? 4 : 9
					});
					navigateToWcPromotionActivity();
				},
			},
			{
				name: 'prediction-market-banner',
				href: `/images/banner/lobby/prediction-market/prediction-market-${shortCode}.jpg`,
				fallbackHref: '/images/banner/lobby/prediction-market/prediction-market-en.jpg',
				enabled: enablePredictionMarketPopup,
				navigate: () => {
					setKafkaNormal({
						note: isfromLoginBanner ? KafkaNote.PredictionMarket : KafkaNote.WcPromotionHub,
						type: 1,
						action: isfromLoginBanner ? 1 : 11
					});
					openPredictionMarket();
				},
			},
			{
				name: 'bet-builder-plus',
				href: `/images/banner/lobby/bet-builder/bet-builder-${shortCode}.jpg`,
				fallbackHref: '/images/banner/lobby/bet-builder/bet-builder-en.jpg',
				enabled: SiteConfigsData?.EnableBetBuilderPlusPopup,
				navigate: () => {
					setKafkaNormal({
						note: isfromLoginBanner ? KafkaNote.BetBuilderPlus : KafkaNote.WcPromotionHub,
						type: 1,
						action: isfromLoginBanner ? 2 : 10
					});
					router.push('/sports/1/l');
					activeFilterBetBuilderPlus();
				},
			},
			{
				name: 'e-soccer-battle',
				href: `/images/banner/home/${shortCode}/kv_better-esoccer-battle.jpg`,
				fallbackHref: '/images/banner/home/en/kv_better-esoccer-battle.jpg',
				refinedAnalyseKey: isfromLoginBanner ? 'switch-to-odds-page-via-login-popup-banner-112' : 'odds-page-integrations-page-banner-112',
				enabled: enableESoccerBattlePromotion,
				navigate: () => {
					setKafkaNormal({
						note: KafkaNote.WcPromotionHub,
						type: 1,
						action: isfromLoginBanner ? 5 : 12
					});
					setCurrentPage('lbl_VirtualSport');
					navigateToSports('/sports/112');
				},
			},
		].filter(b => b.enabled);
	}, [
		SiteConfigsData, locale, user, shortCode,
		enablePredictionMarketPopup, isWcPromotionVisible, router,
		goToWcPromotionMenuUrl, isfromLoginBanner, activeFilterBetBuilderPlus,
		enableESoccerBattlePromotion, navigateToWcPromotionActivity, openPredictionMarket,
		setCurrentPage, switchToCorrectScore,
	]);
};
